import { useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { motion } from 'framer-motion';
import { alaViewport } from './alaMotion';

/** Repli360 American Landmark Portal widget — same embed as alapts.com/resident-resources */
const REPLI_LOADER_SRC =
  'https://master-repli-business-admin.vercel.app/api/widgets/loader.js';
const REPLI_WIDGET_CLASS = 'repli-widget-8792b5f441ee';
const REPLI_SCRIPT_ID = '__ala_repli_portal_widget_loader';

function loadRepliWidget(host: HTMLElement) {
  // Fresh mount point so SPA revisits re-initialize (loader skips nodes with shadowRoot).
  host.replaceChildren();
  const mount = document.createElement('div');
  mount.className = REPLI_WIDGET_CLASS;
  host.appendChild(mount);

  const existing = document.getElementById(REPLI_SCRIPT_ID);
  if (existing) existing.remove();

  const script = document.createElement('script');
  script.id = REPLI_SCRIPT_ID;
  script.src = REPLI_LOADER_SRC;
  script.async = true;
  document.body.appendChild(script);
}

export function ResidentPortalContent() {
  const widgetHostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = widgetHostRef.current;
    if (!host) return;

    loadRepliWidget(host);

    return () => {
      host.replaceChildren();
    };
  }, []);

  return (
    <section className="bg-[rgb(var(--ala-background))] px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto flex w-full max-w-[900px] flex-col items-center gap-8 text-center md:gap-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-4"
        >
          <RichTextContent
            path="residentportalcontent.heading"
            label="Heading"
            as="h2"
            className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] font-bold leading-[1.15] tracking-[-0.02em] text-[rgb(var(--ala-text))]"
          />
          <RichTextContent
            path="residentportalcontent.description"
            label="Description"
            as="div"
            className="mx-auto max-w-[640px] font-body text-base leading-relaxed text-[rgb(var(--ala-text-muted))] md:text-lg [&_p]:m-0"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="w-full"
          aria-label="Resident portal property search"
        >
          <div ref={widgetHostRef} className="min-h-[120px] w-full" />
        </motion.div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentPortalContent',
  displayName: 'Resident Portal Content',
  tokens: {
    'residentportalcontent.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Resident Portal Search</p>',
    },
    'residentportalcontent.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>We are committed to making everyday living simple and convenient. Access your resident portal to manage payments, submit maintenance requests, and stay connected with your community.</p>',
    },
  },
};

(ResidentPortalContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ResidentPortalContent;
